import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function CGV() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'fr';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <button
          onClick={() => navigate('/')}
          className="text-primary-600 hover:text-primary-700 font-semibold mb-6"
        >
          ← {lang === 'fr' ? 'Retour' : 'Back'}
        </button>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          {lang === 'fr' ? 'Conditions Générales de Vente' : 'Terms of Sale'}
        </h1>

        {lang === 'fr' ? (
          <>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong>Dernière mise à jour :</strong> 31 janvier 2026
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Objet</h2>
              <p className="text-gray-700 mb-4">
                Les présentes Conditions Générales de Vente (ci-après « CGV ») régissent les relations contractuelles 
                entre BEL-AN Prod et tout utilisateur souscrivant à un abonnement payant sur la plateforme ProjectBoost.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Offres et tarifs</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Abonnements mensuels</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-gray-700 mb-2">
                  <strong>Abonnement Silver :</strong> 29,99€ TTC/mois
                </p>
                <ul className="list-disc list-inside text-gray-600 ml-4 space-y-1">
                  <li>3 diagnostics par mois</li>
                  <li>Téléchargement PDF des rapports</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-gray-700 mb-2">
                  <strong>Abonnement Gold :</strong> 49,99€ TTC/mois
                </p>
                <ul className="list-disc list-inside text-gray-600 ml-4 space-y-1">
                  <li>7 diagnostics par mois</li>
                  <li>Téléchargement PDF des rapports</li>
                  <li>Accès à la bibliothèque complète</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-4 mt-6">
                Les prix sont indiqués en euros toutes taxes comprises (TTC) et peuvent être modifiés à tout moment. 
                Les modifications de tarifs ne s'appliquent pas aux abonnements en cours.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Commande et paiement</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1. Processus de commande</h3>
              <p className="text-gray-700 mb-4">
                La souscription à un abonnement s'effectue directement sur la Plateforme. 
                Le paiement est traité de manière sécurisée par notre prestataire de paiement Stripe.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2. Moyens de paiement</h3>
              <p className="text-gray-700 mb-4">
                Les paiements peuvent être effectués par :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Carte bancaire (Visa, Mastercard, American Express)</li>
                <li>Autres moyens de paiement proposés par Stripe</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3. Confirmation de commande</h3>
              <p className="text-gray-700 mb-4">
                Un message de confirmation de votre commande vous sera adressée. Vous pourrez télécharger votre facture mensuelle directement sur l'application, voir "gérer mon abonnement".
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Abonnements mensuels</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.1. Durée et renouvellement</h3>
              <p className="text-gray-700 mb-4">
                Les abonnements Silver et Gold sont souscrits pour une durée d'un mois, renouvelable tacitement 
                chaque mois, sauf résiliation.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2. Résiliation</h3>
              <p className="text-gray-700 mb-4">
                Vous pouvez résilier votre abonnement à tout moment depuis votre tableau de bord. La résiliation 
                prendra effet à la fin de la période de facturation en cours. Aucun remboursement ne sera effectué 
                pour la période en cours.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3. Quota de diagnostics</h3>
              <p className="text-gray-700 mb-4">
                Le quota de diagnostics mensuels se renouvelle automatiquement chaque mois. 
                Les diagnostics non utilisés ne sont pas reportés au mois suivant.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Droit de rétractation</h2>
              <p className="text-gray-700 mb-4">
                Conformément à l'article L221-28 du Code de la consommation, vous disposez d'un délai de 14 jours 
                à compter de la souscription pour exercer votre droit de rétractation sans avoir à justifier de motifs.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Exception :</strong> Si vous utilisez un diagnostic pendant la période de rétractation, 
                vous renoncez expressément à votre droit de rétractation pour ce service.
              </p>
              <p className="text-gray-700 mb-4">
                Pour exercer votre droit de rétractation, contactez-nous à : 
                <a href="mailto:ace.direction22@gmail.com" className="text-primary-600 hover:underline ml-1">
                  ace.direction22@gmail.com
                </a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Facturation</h2>
              <p className="text-gray-700 mb-4">
                Les factures sont disponibles dans votre espace personnel. 
                Elles mentionnent :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Le numéro de facture</li>
                <li>La date d'émission</li>
                <li>Le détail des prestations</li>
                <li>Le montant TTC</li>
                <li>Les informations légales de BEL-AN Prod</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Accès aux services</h2>
              <p className="text-gray-700 mb-4">
                L'accès aux services souscrits est effectif immédiatement après validation du paiement.
              </p>
              <p className="text-gray-700 mb-4">
                En cas de non-paiement ou de paiement frauduleux, BEL-AN Prod se réserve le droit de suspendre 
                immédiatement l'accès aux services, sans préavis.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Modification des tarifs</h2>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod se réserve le droit de modifier ses tarifs à tout moment. Les abonnés en cours seront 
                informés par email au moins 30 jours avant l'entrée en vigueur des nouveaux tarifs.
              </p>
              <p className="text-gray-700 mb-4">
                Les nouveaux tarifs ne s'appliqueront qu'au prochain renouvellement de l'abonnement. L'abonné peut 
                résilier avant cette date s'il n'accepte pas les nouveaux tarifs.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Garanties et responsabilités</h2>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod s'engage à fournir les services décrits avec diligence. Toutefois, les diagnostics 
                fournis sont des outils d'aide à la décision et ne constituent pas des garanties de résultats.
              </p>
              <p className="text-gray-700 mb-4">
                La responsabilité de BEL-AN Prod ne pourra être engagée en cas de :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Mauvaise utilisation de la Plateforme</li>
                <li>Décisions prises sur la base des diagnostics</li>
                <li>Interruption temporaire des services pour maintenance</li>
                <li>Force majeure</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Réclamations</h2>
              <p className="text-gray-700 mb-4">
                Pour toute réclamation concernant une commande ou un abonnement, contactez notre service client :
              </p>
              <p className="text-gray-700 mb-2">
                Email : <a href="mailto:ace.direction22@gmail.com" className="text-primary-600 hover:underline">ace.direction22@gmail.com</a>
              </p>
              <p className="text-gray-700 mb-4 mt-4">
                Nous nous engageons à traiter votre réclamation dans un délai de 7 jours ouvrés.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Médiation</h2>
              <p className="text-gray-700 mb-4">
                Conformément aux dispositions du Code de la consommation concernant le règlement amiable des litiges,
                en cas de litige, vous pouvez saisir gratuitement le médiateur de la consommation MEDICYS dans un délai 
                d'un an à compter de votre réclamation écrite auprès de BEL-AN Prod. 
                MEDICYS est accessible sur :{" "}
                <a href="https://www.medicys.fr" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  www.medicys.fr
                </a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">12. Droit applicable</h2>
              <p className="text-gray-700 mb-4">
                Les présentes CGV sont soumises au droit français. En cas de litige, les tribunaux français seront 
                seuls compétents.
              </p>
            </section>
          </>
        ) : (
          <>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong>Last updated:</strong> January 31, 2026
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Purpose</h2>
              <p className="text-gray-700 mb-4">
                These Terms of Sale govern the contractual relationship between BEL-AN Prod and any user subscribing 
                to a paid subscription on the ProjectBoost platform.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Offers and Pricing</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Monthly Subscriptions</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-gray-700 mb-2">
                  <strong>Silver Subscription:</strong> €29.99/month (incl. tax)
                </p>
                <ul className="list-disc list-inside text-gray-600 ml-4 space-y-1">
                  <li>3 diagnostics per month</li>
                  <li>PDF report download</li>
                </ul>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-gray-700 mb-2">
                  <strong>Gold Subscription:</strong> €49.99/month (incl. tax)
                </p>
                <ul className="list-disc list-inside text-gray-600 ml-4 space-y-1">
                  <li>7 diagnostics per month</li>
                  <li>PDF report download</li>
                  <li>Full library access</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-4 mt-6">
                Prices are shown in euros including all taxes and may be modified at any time. 
                Price changes do not apply to current subscriptions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Order and Payment</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1. Order Process</h3>
              <p className="text-gray-700 mb-4">
                Subscription is made directly on the Platform. Payment is processed 
                securely by our payment provider Stripe.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2. Payment Methods</h3>
              <p className="text-gray-700 mb-4">
                Payments can be made by:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Credit card (Visa, Mastercard, American Express)</li>
                <li>Other payment methods offered by Stripe</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3. Order Confirmation</h3>
              <p className="text-gray-700 mb-4">
                An order confirmation will be sent to you by email. You can download your monthly invoice 
                directly in the application, see "Manage my subscription".
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Monthly Subscriptions</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.1. Duration and Renewal</h3>
              <p className="text-gray-700 mb-4">
                Silver and Gold subscriptions are for one month, automatically renewable each month unless canceled.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2. Cancellation</h3>
              <p className="text-gray-700 mb-4">
                You can cancel your subscription at any time from your dashboard. Cancellation will take effect 
                at the end of the current billing period. No refund will be made for the current period.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3. Diagnostic Quota</h3>
              <p className="text-gray-700 mb-4">
                The monthly diagnostic quota automatically renews each month. Unused diagnostics 
                are not carried over to the next month.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Right of Withdrawal</h2>
              <p className="text-gray-700 mb-4">
                You have a 14-day period from subscription to exercise your right of withdrawal without justification.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Exception:</strong> If you use a diagnostic during the withdrawal period, you expressly 
                waive your right of withdrawal for this service.
              </p>
              <p className="text-gray-700 mb-4">
                To exercise your right of withdrawal, contact us at: 
                <a href="mailto:ace.direction22@gmail.com" className="text-primary-600 hover:underline ml-1">
                  ace.direction22@gmail.com
                </a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Invoicing</h2>
              <p className="text-gray-700 mb-4">
                Invoices are available in your personal account. They include:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Invoice number</li>
                <li>Issue date</li>
                <li>Service details</li>
                <li>Total amount (incl. tax)</li>
                <li>BEL-AN Prod legal information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Access to Services</h2>
              <p className="text-gray-700 mb-4">
                Access to subscribed services is effective immediately after payment validation.
              </p>
              <p className="text-gray-700 mb-4">
                In the event of non-payment or fraudulent payment, BEL-AN Prod reserves the right to immediately 
                suspend access to services without notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Price Changes</h2>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod reserves the right to modify its prices at any time. Current subscribers will be 
                notified by email at least 30 days before the new prices take effect.
              </p>
              <p className="text-gray-700 mb-4">
                New prices will only apply at the next subscription renewal. Subscribers may cancel before that 
                date if they do not accept the new prices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Warranties and Liability</h2>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod undertakes to provide the described services with diligence. However, the diagnostics 
                provided are decision-support tools and do not constitute guarantees of results.
              </p>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod cannot be held liable for:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Misuse of the Platform</li>
                <li>Decisions made based on diagnostics</li>
                <li>Temporary service interruptions for maintenance</li>
                <li>Force majeure</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Claims</h2>
              <p className="text-gray-700 mb-4">
                For any claim regarding an order or subscription, contact our customer service:
              </p>
              <p className="text-gray-700 mb-2">
                Email: <a href="mailto:ace.direction22@gmail.com" className="text-primary-600 hover:underline">ace.direction22@gmail.com</a>
              </p>
              <p className="text-gray-700 mb-4 mt-4">
                We undertake to process your claim within 7 business days.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Mediation</h2>
              <p className="text-gray-700 mb-4">
                In accordance with the provisions of the Consumer Code regarding the amicable settlement of disputes, 
                in the event of a dispute, you may contact the consumer mediator MEDICYS free of charge within one year 
                of your written complaint to BEL-AN Prod. MEDICYS is accessible at:{" "}
                <a href="https://www.medicys.fr" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  www.medicys.fr
                </a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">12. Applicable Law</h2>
              <p className="text-gray-700 mb-4">
                These Terms of Sale are governed by French law. In the event of a dispute, French courts shall have 
                sole jurisdiction.
              </p>
            </section>
          </>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>© 2026 BEL-AN Prod - ProjectBoost. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
        </div>
      </div>
    </div>
  );
}

export default CGV;
